<template>
  <div class="optionDropDownSelect">
    <modal
      v-if="isModalVisible"
      @close="closeModal"
      @continueUpdate="continueUpdate"
    />
    <label class="optionDropDownSelect__label">
      {{ formattedOptionName }}
    </label>

    <div>
      <v-select
        :placeholder="defaultOption.PlaceholderText"
        :id="selectId"
        :value="displaySelectedOption"
        :clearable="false"
        :searchable="false"
        :filterable="false"
        :options="memoizedOptions"
        label="Value"
        @input="onInput"
      >
        <template slot="option" slot-scope="option">
          <div
            class="selectionOption"
            :class="memoizedCheckCompatibleOption(defaultOption.Key, option)"
          >
            {{ option.Value }}
          </div>
        </template>
      </v-select>
    </div>
    <div class="optionDropDownSelect__informationLink">
      <a
        :target="defaultOption.HelperData.HelperLink ? '_blank' : ''"
        :href="helperLink"
        @click="
          defaultOption.HelperData.DataAttribute === 'chat' ? callChat() : null
        "
        class="optionDropDownSelect__informationLink__link"
      >
        <img
          v-if="defaultOption.HelperData.HelperText"
          src="https://res.cloudinary.com/legrandav/image/upload/v1707253441/info_outline_small.svg"
          alt="More information"
        />
        <span>{{ defaultOption.HelperData.HelperText }}</span>
      </a>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import vSelect from "vue-select";
import modal from "../Modals/ProductOptionsModal";
import ProductOptionService from "../../../services/ProductOptionService";

export default {
  props: {
    optionName: String,
    defaultOption: Object,
    selectedOption: Object,
    skuOptions: Object,
    selectId: String,
    optionSorting: Boolean
  },
  data() {
    return {
      selectedOptionsModel: {},
      isModalVisible: false,
      currentOptionSelected: {},
      memoizedCompatibleOptions: new Map()
    };
  },
  model: {
    prop: "selectedOption",
    event: "update-dropdown"
  },
  created() {
    this.memoizedCheckCompatibleOption = this.memoize(
      this.checkCompatibleOption
    );
  },
  mounted() {
    this.initializeSelectedOptionsModel();
  },
  computed: {
    ...mapGetters("product", [
      "getSelectedOptions",
      "isSkuPage",
      "isLoading",
      "getOptionDefinitions",
      "getSkus"
    ]),
    helperLink() {
      return this.defaultOption.HelperData.HelperLink
        ? this.defaultOption.HelperData.HelperLink.Url
        : "javascript:void(0);";
    },
    formattedOptionName() {
      return `${this.optionName}:`;
    },
    displaySelectedOption() {
      const selectedOptions = this.getSelectedOptions;
      let selectedOption = "";
      if (selectedOptions?.length) {
        const found = selectedOptions.find(
          option => option.Key === this.optionKey
        );
        if (found) selectedOption = found.Value;
      } else {
        const { query } = this.$route;
        const queryValue = query[this.optionKey];
        if (queryValue && this.options.some(opt => opt.Value === queryValue)) {
          selectedOption = queryValue;
        }
      }
      const decoder = document.createElement("div");
      decoder.innerHTML = selectedOption;
      return decoder.textContent;
    },
    optionKey() {
      return this.defaultOption.Key;
    },
    memoizedOptions() {
      if (!this.skuOptions[this.optionKey]) return [];

      const [storePlaceholderTextValue, ...valuesArray] = this.skuOptions[
        this.optionKey
      ];

      const sorted = this.optionSorting
        ? this.sortByOrder(valuesArray)
        : this.sortByValue(valuesArray);

      return [storePlaceholderTextValue, ...sorted];
    }
  },
  methods: {
    memoize(fn) {
      const cache = new Map();
      return (...args) => {
        const key = JSON.stringify(args);
        if (cache.has(key)) return cache.get(key);
        const result = fn.apply(this, args);
        cache.set(key, result);
        return result;
      };
    },
    sortByOrder(array) {
      return array.sort((a, b) => a.SortOrder - b.SortOrder);
    },
    sortByValue(array) {
      return array.sort(({ Value: a }, { Value: b }) => {
        const parsedA = parseFloat(a);
        const parsedB = parseFloat(b);
        if (isNaN(parsedA) && isNaN(parsedB)) return a.localeCompare(b);
        if (isNaN(parsedA)) return 1;
        if (isNaN(parsedB)) return -1;
        return parsedA - parsedB;
      });
    },
    initializeSelectedOptionsModel() {
      this.selectedOptionsModel = Object.keys(this.skuOptions).reduce(
        (acc, key) => {
          acc[key] = "default";
          return acc;
        },
        {}
      );

      this.getSelectedOptions.forEach(option => {
        if (this.selectedOptionsModel[option.Key]) {
          this.selectedOptionsModel[option.Key] = option.Value;
        }
      });
    },
    onInput(valueOpt) {
      this.currentOptionSelected = valueOpt;

      if (valueOpt.showOption !== "default") {
        const compatibleOptionClass = this.memoizedCheckCompatibleOption(
          this.optionKey,
          valueOpt
        );
        if (compatibleOptionClass === "disabledOption") {
          this.isModalVisible = true;
          return;
        }
      }

      this.$emit("update-dropdown", {
        Key: this.optionKey,
        Value: valueOpt.Value,
        showOption: valueOpt.showOption,
        fromService: false
      });
    },
    checkCompatibleOption(key, option) {
      if (option?.showOption === "default") return "placeholderOption";

      const selectedOptions = this.getSelectedOptions;
      if (selectedOptions.length === 1 && selectedOptions[0].Key === key)
        return "";

      if (!selectedOptions.length) return "";

      const filteredOpts =
        selectedOptions.length > 1
          ? selectedOptions.filter(opt => opt.Key !== key)
          : selectedOptions;

      const cacheKey = JSON.stringify({ key, filteredOpts });
      if (!this.memoizedCompatibleOptions.has(cacheKey)) {
        const compatibleOptions = ProductOptionService.buildCompatibleProductOptions(
          this.getSkus,
          filteredOpts
        );
        this.memoizedCompatibleOptions.set(cacheKey, compatibleOptions);
      }

      const compatibleOptions = this.memoizedCompatibleOptions.get(cacheKey);

      if (
        compatibleOptions?.[key]?.includes(option.Value) &&
        option.showOption !== "default"
      ) {
        return "";
      }

      return "disabledOption";
    },
    continueUpdate() {
      this.$store.dispatch("product/clearSelectedOption").then(() => {
        this.isModalVisible = false;
        this.$emit("update-dropdown", {
          Key: this.optionKey,
          Value: this.currentOptionSelected.Value
        });
      });
    },
    closeModal() {
      this.isModalVisible = false;
    },
    callChat() {
      document.body.dispatchEvent(new CustomEvent("loadsalesforcechat"));
    }
  },
  components: {
    vSelect,
    modal
  }
};
</script>

<style lang="scss">
//$vs-component-line-height: 20px;
// $vs-state-active-bg: #a0e7ee;
$vs-state-active-color: $gray-darker;

$vs-border-radius: 0;
$vs-border-color: $gray-medium;

$vs-controls-color: $gray-darker;
$vs-controls-size: 1, 0.8;

$vs-dropdown-box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);

@import "../../../../node_modules/vue-select/src/scss/vue-select.scss";
</style>
<style lang="scss" scoped>
.optionDropDownSelect {
  letter-spacing: 0.2px;
  line-height: 24px;
  font-family: $body;
  font-size: 20px;
  font-weight: normal;
  margin-bottom: 25px;
  &__label {
    color: $darkgray;
    font-size: 16px;
    font-weight: normal;
    width: 83%;
  }
  &__select {
    color: $gray-darker;
    border: 1px solid $gray-medium;
    padding: 10px 5px;
    height: 50px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black' width='18px' height='18px'%3E%3Cpath d='M0 0h24v24H0V0z' fill='none'/%3E%3Cpath d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'/%3E%3C/svg%3E")
      no-repeat right #fff;
    -webkit-appearance: none;
    -moz-appearance: none;
    -ms-appearance: none;
    appearance: none;
    background-position-x: 98%;

    option {
      border-radius: 0px;
      height: 50px !important;
      color: rgb(51, 51, 51);
      letter-spacing: 0.25px;
    }
  }
  &__labelSelectedOption {
    color: $gray-darker;
    font-weight: 600;
  }
  &__informationLink {
    font-family: $body;
    font-size: 12px;
    letter-spacing: 0.6px;
    line-height: 20px;
    color: $black85;
    cursor: pointer;

    &__link {
      display: flex;
      gap: 2px;
      align-items: center;

      &:hover,
      &:focus {
        color: $neutral300;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }
  }
  &::v-deep .vs__dropdown-menu {
    padding: 0;
    border-top: 1px solid $gray-medium;
    z-index: 5;

    li {
      padding: 0 !important;
    }
    .selectionOption {
      width: 100%;
      height: 100%;
      padding: 10px;
    }
  }

  &::v-deep .vs--open .vs__selected {
    opacity: 1;
    position: initial;
    text-wrap: nowrap !important;
  }
  &::v-deep .vs__dropdown-toggle {
    padding: 6px 14px 6px 0;
    height: 49px;
    overflow: hidden;
  }
  &::v-deep .vs--open .vs__dropdown-toggle {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
  }
  &::v-deep .vs__dropdown-option {
    padding: 10px 10px;
  }

  &::v-deep .vs__dropdown-option--highlight {
    background: $neutral100 !important;
    color: $black85;
  }

  &::v-deep input.vs__search::-webkit-input-placeholder,
  &::v-deep input.vs__search::-moz-placeholder,
  &::v-deep input.vs__search:-ms-input-placeholder,
  &::v-deep input.vs__search:-moz-placeholder,
  &::v-deep input.vs__search::placeholder {
    font-family: $body;
    color: $lightgray;
    letter-spacing: 0.2px;
  }
}
select::-ms-expand {
  display: none;
}
.disabledOption {
  background: rgb(243, 243, 243);
  color: rgba(51, 51, 51, 0.5) !important;
  width: 100%;
  height: 100%;
  padding: 10px;
  font-size: 20px;
  letter-spacing: 0.25px;
  font-family: $body;
}
.placeholderOption {
  color: rgb(153, 153, 153) !important;
  font-size: 20px;
  font-family: $body;
  letter-spacing: 0.25px;
  width: 100%;
  height: 100%;
  padding: 10px;
}
</style>
